import { useState, useCallback, useEffect } from "react";
import { apiGetUsers, apiAddUser, apiUpdateUser, apiDeleteUser, ApiUser } from "@/utils/apiClient";

export type UserRole = "admin" | "editor" | "support";

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("tr-TR", {
      day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch { return "—"; }
}

function PasswordStrength({ password }: { password: string }) {
  const score = [
    password.length >= 8,
    /[A-Z]/.test(password),
    /[0-9]/.test(password),
    /[^A-Za-z0-9]/.test(password),
    password.length >= 12,
  ].filter(Boolean).length;

  const levels = [
    { label: "Çok Zayıf", color: "bg-red-500" },
    { label: "Zayıf", color: "bg-orange-500" },
    { label: "Orta", color: "bg-yellow-500" },
    { label: "İyi", color: "bg-emerald-400" },
    { label: "Güçlü", color: "bg-emerald-500" },
  ];
  const level = levels[Math.max(0, score - 1)];
  if (!password) return null;

  return (
    <div className="mt-2">
      <div className="flex gap-1 mb-1">
        {[1, 2, 3, 4, 5].map((i) => (
          <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= score ? level.color : "bg-[#2B3654]"}`} />
        ))}
      </div>
      <p className="text-xs" style={{ color: score >= 4 ? "#10B981" : score >= 3 ? "#F59E0B" : "#F87171" }}>
        {level?.label}
      </p>
    </div>
  );
}

// ── Change Password Form ──────────────────────────────────────────────────────

function ChangePasswordForm({ currentUserId }: { currentUserId: string }) {
  const [form, setForm] = useState({ current: "", next: "", confirm: "" });
  const [show, setShow] = useState({ current: false, next: false, confirm: false });
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleSave = async () => {
    setErrorMsg("");
    if (form.next.length < 6) { setErrorMsg("Yeni şifre en az 6 karakter olmalı."); setStatus("error"); return; }
    if (form.next !== form.confirm) { setErrorMsg("Şifreler eşleşmiyor."); setStatus("error"); return; }
    setStatus("loading");
    try {
      await apiUpdateUser(currentUserId, { password: form.next });
      setStatus("success");
      setForm({ current: "", next: "", confirm: "" });
      setTimeout(() => setStatus("idle"), 3000);
    } catch (err) {
      setErrorMsg(err instanceof Error ? err.message : "Şifre güncellenemedi.");
      setStatus("error");
    }
  };

  const inp = (key: keyof typeof form, showKey: keyof typeof show, label: string, placeholder: string) => (
    <div>
      <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">{label}</label>
      <div className="relative">
        <input
          type={show[showKey] ? "text" : "password"}
          value={form[key]}
          onChange={(e) => { setForm((p) => ({ ...p, [key]: e.target.value })); setStatus("idle"); setErrorMsg(""); }}
          placeholder={placeholder}
          className="w-full bg-[#0F1729] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2.5 pr-10 outline-none focus:border-[#3B82F6] transition-colors placeholder-[#465886]"
        />
        <button type="button" onClick={() => setShow((p) => ({ ...p, [showKey]: !p[showKey] }))}
          className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3B82F6] cursor-pointer w-4 h-4 flex items-center justify-center">
          <i className={show[showKey] ? "ri-eye-off-line" : "ri-eye-line"}></i>
        </button>
      </div>
    </div>
  );

  return (
    <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-6 space-y-4">
      <div className="flex items-center gap-3 pb-4 border-b border-[#2B3654]">
        <div className="w-8 h-8 flex items-center justify-center bg-[#3B82F6]/20 rounded-lg">
          <i className="ri-lock-password-line text-[#3B82F6]"></i>
        </div>
        <div>
          <h3 className="text-white font-semibold text-sm">Şifre Değiştir</h3>
          <p className="text-[#7B8FAF] text-xs">Kendi hesabın için</p>
        </div>
      </div>
      {inp("current", "current", "Mevcut Şifre", "••••••••")}
      <div>
        {inp("next", "next", "Yeni Şifre", "En az 6 karakter")}
        <PasswordStrength password={form.next} />
      </div>
      {inp("confirm", "confirm", "Yeni Şifre (Tekrar)", "Aynı şifreyi girin")}
      {errorMsg && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-2.5 rounded-lg">
          <i className="ri-error-warning-line text-sm flex-shrink-0"></i>{errorMsg}
        </div>
      )}
      {status === "success" && (
        <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-xs px-3 py-2.5 rounded-lg">
          <i className="ri-check-line text-sm flex-shrink-0"></i>Şifre başarıyla değiştirildi.
        </div>
      )}
      <button onClick={handleSave} disabled={status === "loading"}
        className="w-full py-2.5 bg-[#3B82F6] hover:bg-[#3d4e6a] text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap disabled:opacity-60 flex items-center justify-center gap-2">
        {status === "loading" && <i className="ri-loader-4-line animate-spin"></i>}
        Şifreyi Güncelle
      </button>
    </div>
  );
}

// ── User Card ────────────────────────────────────────────────────────────────

function UserCard({
  user, isCurrentUser, isLastAdmin, onDelete, onRoleChange,
}: {
  user: ApiUser; isCurrentUser: boolean; isLastAdmin: boolean;
  onDelete: (id: string) => void; onRoleChange: (id: string, role: UserRole) => void;
}) {
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [newPwd, setNewPwd] = useState("");
  const [pwdStatus, setPwdStatus] = useState<"idle" | "loading" | "saved">("idle");
  const [deleteLoading, setDeleteLoading] = useState(false);

  const handlePwdReset = async () => {
    if (newPwd.length < 6) return;
    setPwdStatus("loading");
    try {
      await apiUpdateUser(user.id, { password: newPwd });
      setPwdStatus("saved");
      setTimeout(() => { setPwdStatus("idle"); setShowResetConfirm(false); setNewPwd(""); }, 2000);
    } catch { setPwdStatus("idle"); }
  };

  const roleColors: Record<string, string> = {
    admin: "text-amber-400 bg-amber-400/10 border-amber-400/30",
    editor: "text-[#3B82F6] bg-[#3B82F6]/10 border-[#3B82F6]/30",
    support: "text-teal-400 bg-teal-400/10 border-teal-400/30",
  };
  const roleLabels: Record<string, string> = { admin: "Admin", editor: "Editör", support: "Destek Ekibi" };

  return (
    <div className={`bg-[#141E35] border rounded-xl p-5 space-y-4 transition-all ${isCurrentUser ? "border-[#3B82F6]/50" : "border-[#2B3654]"}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm flex-shrink-0 ${isCurrentUser ? "bg-[#3B82F6] text-white" : "bg-[#1E2B4A] text-[#7B8FAF]"}`}>
            {user.displayName.charAt(0).toUpperCase()}
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="text-white font-semibold text-sm">{user.displayName}</p>
              {isCurrentUser && (
                <span className="text-xs bg-[#3B82F6]/20 text-[#3B82F6] border border-[#3B82F6]/40 px-2 py-0.5 rounded-full">Sen</span>
              )}
            </div>
            <p className="text-[#7B8FAF] text-xs">@{user.username}</p>
          </div>
        </div>
        <span className={`text-xs font-medium px-2.5 py-1 rounded-full border flex-shrink-0 ${roleColors[user.role]}`}>
          {roleLabels[user.role]}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 text-xs text-[#7B8FAF]">
        <div>
          <p className="text-[#465886] mb-0.5">Oluşturulma</p>
          <p>{formatDate(user.createdAt)}</p>
        </div>
        <div>
          <p className="text-[#465886] mb-0.5">Son Giriş</p>
          <p>{user.lastLogin ? formatDate(user.lastLogin) : "—"}</p>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 pt-2 border-t border-[#2B3654]">
        {!isCurrentUser && !(user.role === "admin" && isLastAdmin) && (
          <button onClick={() => onRoleChange(user.id, user.role === "admin" ? "editor" : user.role === "editor" ? "support" : "editor")}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#2B3654] text-[#7B8FAF] rounded-lg hover:border-[#3B82F6] hover:text-white transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-shield-user-line"></i>
            {user.role === "admin" ? "Editöre Düşür" : user.role === "editor" ? "Destekle Düşür" : "Editöre Yükselt"}
          </button>
        )}
        {!isCurrentUser && (
          <button onClick={() => setShowResetConfirm((p) => !p)}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-[#2B3654] text-[#7B8FAF] rounded-lg hover:border-amber-500/40 hover:text-amber-400 transition-all cursor-pointer whitespace-nowrap">
            <i className="ri-key-2-line"></i>Şifre Sıfırla
          </button>
        )}
        {!isCurrentUser && !(user.role === "admin" && isLastAdmin) && (
          <button onClick={() => { setDeleteLoading(true); onDelete(user.id); }}
            disabled={deleteLoading}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition-all cursor-pointer whitespace-nowrap ml-auto disabled:opacity-60">
            <i className={deleteLoading ? "ri-loader-4-line animate-spin" : "ri-delete-bin-line"}></i>Sil
          </button>
        )}
      </div>

      {showResetConfirm && (
        <div className="bg-[#0F1729] rounded-lg p-3 space-y-2 border border-[#2B3654]">
          <p className="text-[#7B8FAF] text-xs">Yeni şifre belirle:</p>
          <div className="flex gap-2">
            <input type="text" value={newPwd} onChange={(e) => setNewPwd(e.target.value)}
              placeholder="En az 6 karakter"
              className="flex-1 bg-[#141E35] border border-[#2B3654] text-white text-sm rounded-lg px-3 py-2 outline-none focus:border-[#3B82F6]" />
            <button onClick={handlePwdReset} disabled={newPwd.length < 6 || pwdStatus === "loading"}
              className="px-3 py-2 text-xs font-medium rounded-lg cursor-pointer whitespace-nowrap transition-colors bg-amber-500/20 text-amber-400 border border-amber-500/30 hover:bg-amber-500/30 disabled:opacity-40">
              {pwdStatus === "loading" ? <i className="ri-loader-4-line animate-spin"></i> : pwdStatus === "saved" ? "✓ Kaydedildi" : "Uygula"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Add User Form ────────────────────────────────────────────────────────────

function AddUserForm({ onAdded }: { onAdded: () => void }) {
  const [form, setForm] = useState({ displayName: "", username: "", password: "", role: "editor" as UserRole });
  const [showPwd, setShowPwd] = useState(false);
  const [showPanel, setShowPanel] = useState(false);
  const [errors, setErrors] = useState<Partial<Record<string, string>>>({});
  const [status, setStatus] = useState<"idle" | "loading" | "success">("idle");
  const [apiErr, setApiErr] = useState("");

  const handleAdd = async () => {
    const e: Partial<Record<string, string>> = {};
    if (!form.displayName.trim()) e.displayName = "Ad zorunlu";
    if (!form.username.trim()) e.username = "Kullanıcı adı zorunlu";
    if (form.password.length < 6) e.password = "En az 6 karakter olmalı";
    setErrors(e);
    if (Object.keys(e).length > 0) return;

    setStatus("loading");
    setApiErr("");
    try {
      await apiAddUser({ username: form.username.trim(), displayName: form.displayName.trim(), password: form.password, role: form.role });
      setStatus("success");
      setForm({ displayName: "", username: "", password: "", role: "editor" });
      setTimeout(() => { setStatus("idle"); onAdded(); }, 1500);
    } catch (err) {
      setApiErr(err instanceof Error ? err.message : "Kullanıcı eklenemedi");
      setStatus("idle");
    }
  };

  const fieldCls = "w-full bg-[#0F1729] border text-white text-sm rounded-lg px-3 py-2.5 outline-none focus:border-[#3B82F6] transition-colors";

  return (
    <div className="bg-[#141E35] border border-[#2B3654] rounded-xl overflow-hidden">
      <button onClick={() => setShowPanel((p) => !p)}
        className="w-full flex items-center justify-between px-6 py-4 cursor-pointer hover:bg-white/3 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 flex items-center justify-center bg-emerald-500/20 rounded-lg">
            <i className="ri-user-add-line text-emerald-400"></i>
          </div>
          <div className="text-left">
            <p className="text-white font-semibold text-sm">Yeni Kullanıcı Ekle</p>
            <p className="text-[#7B8FAF] text-xs">Admin veya editör rolüyle</p>
          </div>
        </div>
        <div className="w-5 h-5 flex items-center justify-center text-[#7B8FAF]">
          <i className={`${showPanel ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-lg`}></i>
        </div>
      </button>

      {showPanel && (
        <div className="px-6 pb-6 space-y-4 border-t border-[#2B3654] pt-5">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Görünen Ad</label>
              <input type="text" value={form.displayName}
                onChange={(e) => setForm((p) => ({ ...p, displayName: e.target.value }))}
                placeholder="Örn: Ahmet Yılmaz"
                className={`${fieldCls} ${errors.displayName ? "border-red-500/60" : "border-[#2B3654]"}`} />
              {errors.displayName && <p className="text-red-400 text-xs mt-1">{errors.displayName}</p>}
            </div>
            <div>
              <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Kullanıcı Adı</label>
              <input type="text" value={form.username}
                onChange={(e) => setForm((p) => ({ ...p, username: e.target.value.replace(/\s/g, "") }))}
                placeholder="Örn: ahmet"
                className={`${fieldCls} ${errors.username ? "border-red-500/60" : "border-[#2B3654]"}`} />
              {errors.username && <p className="text-red-400 text-xs mt-1">{errors.username}</p>}
            </div>
          </div>
          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-1.5">Şifre</label>
            <div className="relative">
              <input type={showPwd ? "text" : "password"} value={form.password}
                onChange={(e) => setForm((p) => ({ ...p, password: e.target.value }))}
                placeholder="En az 6 karakter"
                className={`${fieldCls} pr-10 ${errors.password ? "border-red-500/60" : "border-[#2B3654]"}`} />
              <button type="button" onClick={() => setShowPwd((p) => !p)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-[#3B82F6] cursor-pointer w-4 h-4 flex items-center justify-center">
                <i className={showPwd ? "ri-eye-off-line" : "ri-eye-line"}></i>
              </button>
            </div>
            {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            <PasswordStrength password={form.password} />
          </div>

          <div>
            <label className="block text-[#7B8FAF] text-xs uppercase tracking-widest mb-2">Rol</label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              {([
                { value: "editor", label: "Editör", desc: "İçerik düzenleyebilir", icon: "ri-pencil-line" },
                { value: "admin", label: "Admin", desc: "Tam yetki", icon: "ri-shield-user-line" },
                { value: "support", label: "Destek Ekibi", desc: "Sadece mesajlar", icon: "ri-customer-service-2-line" },
              ] as const).map((r) => (
                <button key={r.value} onClick={() => setForm((p) => ({ ...p, role: r.value as UserRole }))}
                  className={`flex items-start gap-3 px-4 py-3 rounded-lg border text-left transition-all cursor-pointer ${form.role === r.value ? "border-[#3B82F6] bg-[#3B82F6]/10" : "border-[#2B3654] hover:border-[#3B82F6]/50"}`}>
                  <div className={`w-5 h-5 flex items-center justify-center mt-0.5 flex-shrink-0 ${form.role === r.value ? "text-[#3B82F6]" : "text-[#7B8FAF]"}`}>
                    <i className={`${r.icon} text-sm`}></i>
                  </div>
                  <div>
                    <p className={`text-sm font-medium ${form.role === r.value ? "text-white" : "text-[#7B8FAF]"}`}>{r.label}</p>
                    <p className="text-[#465886] text-xs">{r.desc}</p>
                  </div>
                </button>
              ))}
            </div>
            {form.role === "support" && (
              <div className="mt-2 flex items-start gap-2 bg-teal-500/10 border border-teal-500/25 rounded-lg px-3 py-2">
                <i className="ri-information-line text-teal-400 text-sm mt-0.5 flex-shrink-0"></i>
                <p className="text-teal-300 text-xs">Destek Ekibi rolü sadece Mesajlar ve Canlı Destek paneline erişebilir. Diğer içeriklere erişim yoktur.</p>
              </div>
            )}
          </div>

          {apiErr && (
            <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-xs px-3 py-2.5 rounded-lg">
              <i className="ri-error-warning-line flex-shrink-0"></i>{apiErr}
            </div>
          )}

          {status === "success" ? (
            <div className="flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 text-emerald-400 text-sm px-4 py-3 rounded-lg">
              <i className="ri-check-line"></i>Kullanıcı başarıyla eklendi!
            </div>
          ) : (
            <button onClick={handleAdd} disabled={status === "loading"}
              className="w-full py-2.5 bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-2 disabled:opacity-60">
              {status === "loading" && <i className="ri-loader-4-line animate-spin"></i>}
              Kullanıcı Oluştur
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main Component ───────────────────────────────────────────────────────────

interface UsersManagerProps {
  currentUserId: string;
  currentRole: UserRole;
}

export default function UsersManager({ currentUserId, currentRole }: UsersManagerProps) {
  const [users, setUsers] = useState<ApiUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const data = await apiGetUsers();
      setUsers(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadUsers(); }, [loadUsers]);

  const adminCount = users.filter((u) => u.role === "admin").length;

  const handleDelete = async (id: string) => {
    try {
      await apiDeleteUser(id);
      setUsers((prev) => prev.filter((u) => u.id !== id));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Silme başarısız");
    }
  };

  const handleRoleChange = async (id: string, role: UserRole) => {
    try {
      await apiUpdateUser(id, { role });
      setUsers((prev) => prev.map((u) => (u.id === id ? { ...u, role } : u)));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Rol güncellenemedi");
    }
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-32">
        <i className="ri-loader-4-line animate-spin text-3xl text-[#3B82F6]"></i>
        <p className="text-[#7B8FAF] text-sm mt-3">Kullanıcılar yükleniyor...</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white tracking-tight">Kullanıcılar &amp; Güvenlik</h1>
          <p className="text-[#7B8FAF] text-sm mt-0.5">
            {users.length} kullanıcı · Şifre değiştir, yeni kullanıcı ekle, rolleri yönet
          </p>
        </div>
        <button onClick={loadUsers} disabled={loading}
          className="w-9 h-9 flex items-center justify-center border border-[#2B3654] text-[#7B8FAF] rounded-lg hover:border-[#3B82F6] hover:text-white transition-all cursor-pointer disabled:opacity-40">
          <i className={`ri-refresh-line text-base ${loading ? "animate-spin" : ""}`}></i>
        </button>
      </div>

      {error && (
        <div className="flex items-center gap-2 bg-red-500/10 border border-red-500/30 text-red-400 text-sm px-4 py-3 rounded-lg">
          <i className="ri-error-warning-line flex-shrink-0"></i>
          {error}
          <button onClick={loadUsers} className="ml-auto underline text-xs cursor-pointer">Yeniden dene</button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-3 gap-4">
        {[
          { label: "Toplam Kullanıcı", value: users.length, icon: "ri-team-line", color: "text-[#3B82F6]" },
          { label: "Admin", value: adminCount, icon: "ri-shield-user-line", color: "text-amber-400" },
          { label: "Editör", value: users.filter((u) => u.role === "editor").length, icon: "ri-pencil-line", color: "text-emerald-400" },
          { label: "Destek Ekibi", value: users.filter((u) => u.role === "support").length, icon: "ri-customer-service-2-line", color: "text-teal-400" },
        ].map((s) => (
          <div key={s.label} className="bg-[#141E35] border border-[#2B3654] rounded-xl p-4 flex items-center gap-3">
            <div className={`w-10 h-10 flex items-center justify-center bg-[#0F1729] rounded-lg flex-shrink-0 ${s.color}`}>
              <i className={`${s.icon} text-xl`}></i>
            </div>
            <div>
              <p className="text-2xl font-bold text-white leading-none">{s.value}</p>
              <p className="text-[#7B8FAF] text-xs mt-0.5">{s.label}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <ChangePasswordForm currentUserId={currentUserId} />
        {currentRole === "admin" && <AddUserForm onAdded={loadUsers} />}
      </div>

      {/* User List */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-white font-semibold">Kullanıcı Listesi</h2>
          <span className="text-[#7B8FAF] text-xs">{users.length} kullanıcı</span>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {users.map((user) => (
            <UserCard
              key={user.id}
              user={user}
              isCurrentUser={user.id === currentUserId}
              isLastAdmin={user.role === "admin" && adminCount <= 1}
              onDelete={handleDelete}
              onRoleChange={handleRoleChange}
            />
          ))}
        </div>
      </div>

      {/* Security Tips */}
      <div className="bg-[#141E35] border border-[#2B3654] rounded-xl p-5">
        <div className="flex items-center gap-2 mb-3">
          <div className="w-5 h-5 flex items-center justify-center text-amber-400">
            <i className="ri-shield-check-line text-base"></i>
          </div>
          <p className="text-white text-sm font-semibold">Güvenlik İpuçları</p>
        </div>
        <ul className="space-y-2">
          {[
            "Şifrelerinizi düzenli aralıklarla (3–6 ayda bir) değiştirin.",
            "Her kullanıcıya yalnızca ihtiyacı olan yetkiyi verin.",
            "Şifreler büyük/küçük harf, rakam ve özel karakter içermelidir.",
            "Hesap erişimi olmayan kişilerin kullanıcı kaydını silin.",
          ].map((tip) => (
            <li key={tip} className="flex items-start gap-2 text-[#7B8FAF] text-xs">
              <i className="ri-check-double-line text-emerald-500 mt-0.5 flex-shrink-0"></i>{tip}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}
